import {transparentize} from 'polished';
import React, {FC, useState} from 'react';
import styled from 'styled-components';

import {
  buildHoverParentClassName,
  hoverSelector,
} from '../../../../libs/shared/core/src/helpers/browser/hoverHelpers';
import {TooltipCoordinator} from '../../../../libs/shared/ui/src/components/tooltips/tooltipCoordinator';
import {TooltipOverflowCondition} from '../../../../libs/shared/ui/src/components/tooltips/tooltipOverflowCondition';
import {fontSizes} from '../../../../libs/shared/ui/src/styles/fontStyles';
import {AccountModel} from '../../../models/api/accountModel';
import {RemoveEmailDomainsContainerDialog} from '../dialogs/removeEmailDomainsDialogContainer';
import {
  AccountDetailsEditActionsEnum,
  AccountDetailsEditAttributesEnum,
  buildAccountDetailsEditPayload,
} from '../tracking/accountDetailsTrackingHelpers';
import {useAccountDetailsContext} from './accountDetailsContext';
import {EmailDomainDeleteButtonContainer} from './accountInformation/emailDomainDeleteButtonContainer';
import {EmailDomainEditButtonPopoverCoordinatorContainer} from './accountInformation/emailDomainEditButtonPopoverCoordinatorContainer';

/*
 * Props.
 */

interface AccountDetailsHeaderDomainsProps {
  account: AccountModel;
  shouldDisableActions?: boolean;
}

/*
 * Style.
 */

const StyledAccountDetailsHeaderDomainsContainerDiv = styled.div`
  position: relative;
`;

const StyledAccountDomainsSpan = styled.span`
  font-size: ${fontSizes.medium};
  color: ${(p) => p.theme.greys.shade70};
`;

const StyledControlsDiv = styled.div`
  display: flex;
  opacity: 0;
  position: absolute;
  right: 0;
  top: -4px;
  padding-left: 16px;
  background: linear-gradient(
    to right,
    ${(p) => transparentize(1, p.theme.foreground)} 0%,
    ${(p) => p.theme.foreground} 16px
  );

  ${hoverSelector} {
    opacity: 1;
  }
`;

/*
 * Component.
 */

export const AccountDetailsHeaderDomains: FC<AccountDetailsHeaderDomainsProps> = (props) => {
  const {account, shouldDisableActions} = props;
  const {panelId, onTrackEvent} = useAccountDetailsContext();

  const domainsString = account.domains.join(', ');
  const [isViewingRemoveDialog, setIsViewingRemoveDialog] = useState(false);

  if (domainsString === '') {
    return null;
  }

  return (
    <StyledAccountDetailsHeaderDomainsContainerDiv className={buildHoverParentClassName()}>
      <TooltipCoordinator render={() => domainsString} placement="top">
        <TooltipOverflowCondition>
          <StyledAccountDomainsSpan>{domainsString}</StyledAccountDomainsSpan>
        </TooltipOverflowCondition>
      </TooltipCoordinator>
      {!shouldDisableActions && (
        <StyledControlsDiv>
          <EmailDomainEditButtonPopoverCoordinatorContainer panelId={panelId} account={account} />
          <EmailDomainDeleteButtonContainer
            account={account}
            onClick={() => setIsViewingRemoveDialog(true)}
          />
        </StyledControlsDiv>
      )}
      {isViewingRemoveDialog && (
        <RemoveEmailDomainsContainerDialog
          account={account}
          panelId={panelId}
          onClose={(domainsDeleted) => {
            if (domainsDeleted) {
              onTrackEvent(
                buildAccountDetailsEditPayload(
                  account.id,
                  AccountDetailsEditActionsEnum.DELETE,
                  AccountDetailsEditAttributesEnum.DOMAINS,
                ),
              );
            }
            setIsViewingRemoveDialog(false);
          }}
        />
      )}
    </StyledAccountDetailsHeaderDomainsContainerDiv>
  );
};
